import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useOrgMembers, useUpdateMemberRole, useRenameMember, useRemoveMember,
  useOrgInvites, useCreateInvite, useRevokeInvite, useUnitCatalogue,
  useMe, useTeams, useCreateMembers, useSetMemberEmail, useResetMemberSignIn, type NewMember,
} from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { QualChip } from '../../components/QualChip.js';
import { MemberUnitEditor } from '../../components/MemberUnitEditor.js';
import { LeaveCard, LeaveTag } from '../../components/LeaveCard.js';
import { LEVEL_LABEL } from '../../lib/unit.js';
import type { OrgMember, Invite, UserRole, UnitCatalogue, Team } from '@keetcheck/shared';

// Org-wide roles. Team leadership is per-team, set on the Teams tab.
const ROLES: UserRole[] = ['team_member', 'admin'];

export function MembersPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { data: members = [], isLoading } = useOrgMembers(orgId);
  const { data: invites = [] } = useOrgInvites(orgId);
  const updateRoleMut = useUpdateMemberRole(orgId);
  const removeMut = useRemoveMember(orgId);
  const renameMut = useRenameMember(orgId);
  const revokeMut = useRevokeInvite(orgId);
  const createMut = useCreateInvite(orgId);
  const { data: catalogue } = useUnitCatalogue(orgId);
  const { data: me } = useMe();
  const { data: teams = [] } = useTeams(orgId);
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('team_member');
  const [expiresInHours, setExpiresInHours] = useState(48);
  const [created, setCreated] = useState<Invite | null>(null);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createMut.mutate(
      { email: email.trim(), displayName: displayName.trim(), role, expiresInHours },
      {
        onSuccess: (invite) => {
          setCreated(invite);
          setEmail(''); setDisplayName(''); setRole('team_member');
          setShowForm(false);
        },
      },
    );
  }

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      {/* toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: theme.bg,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          {members.length} member{members.length !== 1 ? 's' : ''}
          {members.some((m) => !m.hasApp) && ` · ${members.filter((m) => !m.hasApp).length} without the app`}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setShowForm(!showForm); setShowAdd(false); }} style={smallBtn}>
            <Icon name="link" size={14} /> Invite
          </button>
          <button onClick={() => { setShowAdd(!showAdd); setShowForm(false); }} style={addBtn}>
            <Icon name="plus" size={15} /> Add member
          </button>
        </div>
      </div>

      {showAdd && <AddMembers orgId={orgId} teams={teams} onClose={() => setShowAdd(false)} />}

      {/* invite form */}
      {showForm && (
        <form onSubmit={handleCreate} style={{
          padding: '14px 16px', background: theme.surface2,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <input
            required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address" style={inputStyle}
          />
          <input
            required value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Full name" style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} style={{ ...inputStyle, flex: 1 }}>
              {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
            <select
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(Number(e.target.value))}
              style={{ ...inputStyle, flex: 1 }}
            >
              <option value={24}>Expires in 24 hours</option>
              <option value={48}>Expires in 48 hours</option>
              <option value={168}>Expires in 7 days</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setShowForm(false)} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={createMut.isPending} style={submitBtnStyle}>
              {createMut.isPending ? 'Creating…' : 'Create invite'}
            </button>
          </div>
        </form>
      )}

      {/* freshly created invite — link + QR */}
      {created && (
        <div style={{
          padding: '16px', background: theme.status.service.bg,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.status.service.fg }}>
            Invite created for {created.displayName} ({created.email}) — share this link before it expires.
          </div>
          <div style={{ background: '#fff', padding: 12, borderRadius: 12 }}>
            <QRCodeSVG value={created.url} size={160} />
          </div>
          <ShareInvite url={created.url} name={created.displayName} />
          <button type="button" onClick={() => setCreated(null)} style={{ ...cancelBtn, maxWidth: 200 }}>
            Done
          </button>
        </div>
      )}

      {/* outstanding invites */}
      {invites.length > 0 && (
        <>
          <SectionHeader>Outstanding invites</SectionHeader>
          {invites.map((invite, i) => (
            <div
              key={invite.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 18px',
                borderBottom: i < invites.length - 1 ? `1px solid ${theme.border}` : 'none',
              }}
            >
              <Icon name="clock" size={16} style={{ color: theme.textMuted, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>
                  {invite.userId ? `${invite.displayName} · ` : ''}{invite.email}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  {roleLabel(invite.role)} · expires {new Date(invite.expiresAt).toLocaleString()}
                </div>
              </div>
              <ShareInvite url={invite.url} name={invite.displayName} compact />
              <button onClick={() => revokeMut.mutate(invite.id)} title="Cancel invite" aria-label="Cancel invite" style={iconBtnSmall}>
                <Icon name="x" size={13} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* current members */}
      <SectionHeader>Members</SectionHeader>
      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && members.length === 0 && <EmptyMsg>No members yet.</EmptyMsg>}
      {members.map((m, i) => (
        <div key={m.userId}>
          <MemberRow
            member={m}
            catalogue={catalogue}
            isSelf={m.userId === me?.id}
            last={i === members.length - 1 && openUserId !== m.userId}
            open={openUserId === m.userId}
            onToggleOpen={() => setOpenUserId(openUserId === m.userId ? null : m.userId)}
            onChangeRole={(newRole) => updateRoleMut.mutate({ userId: m.userId, role: newRole })}
            onRename={(displayName, done) => renameMut.mutate({ userId: m.userId, displayName }, { onSuccess: done })}
            onRemove={() => removeMut.mutate(m.userId)}
          />
          {openUserId === m.userId && <MemberAppAccess orgId={orgId} member={m} />}
          {openUserId === m.userId && <LeaveCard orgId={orgId} userId={m.userId} name={m.displayName} />}
          {openUserId === m.userId && catalogue && (
            <MemberUnitEditor orgId={orgId} member={m} catalogue={catalogue} onDone={() => setOpenUserId(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

function MemberRow({
  member, catalogue, isSelf, last, open, onToggleOpen, onChangeRole, onRename, onRemove,
}: {
  member: OrgMember; catalogue: UnitCatalogue | undefined; isSelf: boolean; last: boolean;
  open: boolean; onToggleOpen: () => void;
  onChangeRole: (role: UserRole) => void;
  onRename: (displayName: string, done: () => void) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(member.displayName);

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) onRename(draft.trim(), () => setEditing(false));
        }}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          borderBottom: last ? 'none' : `1px solid ${theme.border}`,
        }}
      >
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={100}
          aria-label={`New name for ${member.displayName}`} style={{ ...inputStyle, flex: 1 }} />
        <button type="submit" disabled={!draft.trim()} style={{ ...iconBtnSmall, width: 'auto', padding: '0 10px' }}>Save</button>
        <button type="button" onClick={() => { setDraft(member.displayName); setEditing(false); }}
          style={{ ...iconBtnSmall, width: 'auto', padding: '0 10px' }}>Cancel</button>
      </form>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 18px',
      borderBottom: last ? 'none' : `1px solid ${theme.border}`,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 15,
        background: theme.surface2, border: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: theme.textDim, flexShrink: 0,
      }}>
        {member.displayName.slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <button onClick={() => setEditing(true)} title="Rename" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0, background: 'none', border: 'none',
          cursor: 'pointer', fontFamily: theme.font, fontSize: 14, color: theme.text, fontWeight: 500,
        }}>
          {member.displayName}
          <Icon name="edit" size={12} style={{ color: theme.textMuted }} />
        </button>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          {!member.hasApp && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, marginRight: 6,
              background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}`,
            }}>{member.pendingInvite ? 'Invited' : 'No app'}</span>
          )}
          {member.leave && <span style={{ marginRight: 6 }}><LeaveTag leave={member.leave} /></span>}
          {member.emailSuppressed && (
            <span title={member.emailSuppressed === 'bounce'
              ? 'Email to this address bounced, so MusterTap no longer sends to it. Fix the address to clear this.'
              : 'They marked a MusterTap email as spam, so it no longer emails them.'} style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, marginRight: 6,
              background: theme.status.danger.bg, color: theme.status.danger.fg,
            }}>{member.emailSuppressed === 'bounce' ? 'Email bounced' : 'Marked spam'}</span>
          )}
          {member.email ?? 'no email'}
          {' · '}
          {member.teamName
            ? `${member.teamName}${member.teamRole && member.teamRole !== 'member' ? ` (${member.teamRole})` : ''}`
            : 'no team'}
        </div>
        <UnitSummary member={member} catalogue={catalogue} />
      </div>
      <button aria-label="Unit details" onClick={onToggleOpen} title="Unit details" aria-expanded={open} style={iconBtnSmall}>
        <Icon name={open ? 'chevronD' : 'chevron'} size={13} />
      </button>
      <select
        value={member.role}
        disabled={isSelf}
        onChange={(e) => onChangeRole(e.target.value as UserRole)}
        style={{ ...inputStyle, width: 130, padding: '5px 8px', fontSize: 12 }}
      >
        {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
      </select>
      {!isSelf && (
        <button aria-label={`Remove ${member.displayName}`} title="Remove from unit" onClick={onRemove} style={iconBtnSmall}>
          <Icon name="trash" size={12} />
        </button>
      )}
    </div>
  );
}

// ── Adding members without the app ────────────────────────────

/** One "Name[, Team][, email]" per line; commas or tabs (a spreadsheet paste). */
function parseList(text: string, teams: Team[]): { rows: NewMember[]; problems: string[] } {
  const byName = new Map(teams.map((t) => [t.name.trim().toLowerCase(), t.id]));
  const rows: NewMember[] = [];
  const problems: string[] = [];
  text.split(/\r?\n/).forEach((line, i) => {
    const cells = line.split(/\t|,/).map((c) => c.trim()).filter((c, j) => j === 0 || c);
    const name = cells[0];
    if (!name) return;
    const row: NewMember = { displayName: name };
    for (const c of cells.slice(1)) {
      if (c.includes('@')) row.email = c;
      else if (byName.has(c.toLowerCase())) row.teamId = byName.get(c.toLowerCase())!;
      else problems.push(`Line ${i + 1}: no team called “${c}”`);
    }
    rows.push(row);
  });
  return { rows, problems };
}

function AddMembers({ orgId, teams, onClose }: { orgId: string; teams: Team[]; onClose: () => void }) {
  const createMut = useCreateMembers(orgId);
  const [mode, setMode] = useState<'one' | 'list'>('one');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState('');
  const [list, setList] = useState('');
  const [result, setResult] = useState<{ created: number; skipped: string[] } | null>(null);
  const parsed = parseList(list, teams);

  function submit(rows: NewMember[]) {
    setResult(null);
    createMut.mutate(rows, {
      onSuccess: (r) => {
        setResult({ created: r.created.length, skipped: r.skipped });
        setName(''); setEmail(''); setList('');
      },
    });
  }

  return (
    <div style={{
      padding: '14px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: 12, color: theme.textDim }}>
        Members you add here are on the roster straight away. They don't need an email or the app —
        open a member later to add their email and invite them.
      </div>
      <div role="radiogroup" style={{ display: 'flex', gap: 6 }}>
        {([['one', 'One member'], ['list', 'Paste a list']] as const).map(([m, label]) => (
          <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} style={chip(mode === m)}>
            {label}
          </button>
        ))}
      </div>

      {mode === 'one' ? (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) submit([{ displayName: name.trim(), email: email.trim() || null, teamId: teamId || null }]);
        }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input required value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Full name" style={inputStyle} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} aria-label="Team" style={{ ...inputStyle, flex: '1 1 140px' }}>
              <option value="">No team</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)"
              style={{ ...inputStyle, flex: '2 1 200px' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Close</button>
            <button type="submit" disabled={createMut.isPending || !name.trim()} style={submitBtnStyle}>
              {createMut.isPending ? 'Adding…' : 'Add member'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <textarea value={list} onChange={(e) => setList(e.target.value)} rows={8} aria-label="Member list"
            placeholder={'One member per line — name, then team and email if you have them:\nJane Citizen, Blue\nSam Lee, Yellow, sam@example.com\nAlex Brown'}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.font }} />
          {parsed.rows.length > 0 && (
            <div style={{ fontSize: 12, color: theme.textDim }}>
              {parsed.rows.length} member{parsed.rows.length !== 1 ? 's' : ''} ·{' '}
              {parsed.rows.filter((r) => r.teamId).length} with a team · {parsed.rows.filter((r) => r.email).length} with an email.
              Names already in the unit are skipped.
            </div>
          )}
          {parsed.problems.length > 0 && (
            <div style={{ fontSize: 12, color: theme.status.danger.fg }}>
              {parsed.problems.slice(0, 5).map((p) => <div key={p}>{p}</div>)}
              {parsed.problems.length > 5 && <div>+ {parsed.problems.length - 5} more</div>}
              <div>Fix the team names (or remove them) to continue.</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Close</button>
            <button type="button" onClick={() => submit(parsed.rows)}
              disabled={createMut.isPending || !parsed.rows.length || parsed.problems.length > 0} style={submitBtnStyle}>
              {createMut.isPending ? 'Adding…' : `Add ${parsed.rows.length || ''} member${parsed.rows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </>
      )}
      {createMut.error && <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{(createMut.error as Error).message}</div>}
      {result && (
        <div style={{ fontSize: 12, color: theme.status.service.fg }}>
          Added {result.created} member{result.created !== 1 ? 's' : ''}.
          {result.skipped.length > 0 && ` Skipped (already in the unit): ${result.skipped.join(', ')}.`}
        </div>
      )}
    </div>
  );
}

/** Email and app access for one member: change email, invite a roster-only member. */
function MemberAppAccess({ orgId, member }: { orgId: string; member: OrgMember }) {
  const emailMut = useSetMemberEmail(orgId);
  const inviteMut = useCreateInvite(orgId);
  const resetMut = useResetMemberSignIn(orgId);
  const { data: invites = [] } = useOrgInvites(orgId);
  // The live link: one just made here, or the member's outstanding invite.
  const pendingUrl = invites.find((i) => i.id === member.pendingInvite?.id)?.url ?? null;
  const link = inviteMut.data && inviteMut.data.userId === member.userId ? inviteMut.data.url : pendingUrl;
  const [email, setEmail] = useState(member.email ?? '');
  const changed = email.trim().toLowerCase() !== (member.email ?? '').toLowerCase();
  const error = (emailMut.error ?? inviteMut.error ?? resetMut.error) as Error | null;

  return (
    <div style={{ padding: '12px 18px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted }}>
        {member.hasApp ? 'Uses the app' : 'Doesn’t use the app'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email"
          placeholder={member.hasApp ? 'Email' : 'Email (optional)'} style={{ ...inputStyle, flex: '1 1 220px' }} />
        <button type="button" disabled={!changed || emailMut.isPending || (member.hasApp && !email.trim())}
          onClick={() => emailMut.mutate({ userId: member.userId, email: email.trim() || null })}
          style={{ ...smallBtn, opacity: changed ? 1 : 0.5 }}>
          {emailMut.isPending ? 'Saving…' : 'Save email'}
        </button>
        {!member.hasApp && (
          <button type="button" disabled={!email.trim() || inviteMut.isPending}
            onClick={() => inviteMut.mutate(
              { userId: member.userId, email: email.trim(), displayName: member.displayName, role: member.role, expiresInHours: 168 },
            )}
            style={link ? { ...smallBtn, opacity: email.trim() ? 1 : 0.5 } : { ...addBtn, opacity: email.trim() ? 1 : 0.5 }}>
            <Icon name="link" size={14} /> {link ? 'New link' : 'Invite to app'}
          </button>
        )}
      </div>
      {!member.hasApp && link && <ShareInvite url={link} name={member.displayName} />}
      {member.hasApp && (resetMut.data ? (
        <TempPassword password={resetMut.data.tempPassword} />
      ) : (
        <div>
          <button type="button" disabled={resetMut.isPending}
            onClick={() => {
              if (window.confirm(`Reset ${member.displayName}’s sign-in? Their current password stops working and they’re signed out everywhere. You’ll get a temporary password to pass on.`)) {
                resetMut.mutate(member.userId);
              }
            }}
            style={smallBtn}>
            {resetMut.isPending ? 'Resetting…' : 'Reset sign-in'}
          </button>
        </div>
      ))}
      <div style={{ fontSize: 12, color: theme.textMuted }}>
        {member.hasApp
          ? resetMut.data
            ? `Send this to ${member.displayName.split(' ')[0]} — it’s shown once. They sign in with ${resetMut.data.email ?? 'their email'} and this password, then choose a new one.`
            : 'Changing it changes the email they sign in with. Locked out (e.g. never saw their temporary password)? Reset sign-in gives them a new one.'
          : member.pendingInvite
            ? `Invite link for ${member.pendingInvite.email}, valid until ${new Date(member.pendingInvite.expiresAt).toLocaleDateString()}. MusterTap doesn’t email it — send it to them. When they accept, the app login joins this record (roster, quals and history stay). A new link cancels the old one.`
            : 'They stay on the roster either way. Their team leader handles cover requests and notifications for them until they join.'}
      </div>
      {error && <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{error.message}</div>}
    </div>
  );
}

/** A one-time temporary password from Reset sign-in, with a copy button. */
function TempPassword({ password }: { password: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(password); }
    catch { window.prompt('Copy this password', password); return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div style={{ display: 'flex', gap: 6, width: '100%', maxWidth: 400, flexWrap: 'wrap' }}>
      <input readOnly value={password} aria-label="Temporary password" onFocus={(e) => e.target.select()}
        style={{ ...inputStyle, flex: '1 1 180px', fontSize: 13, fontFamily: 'monospace' }} />
      <button type="button" onClick={() => void copy()} title="Copy password" style={{ ...smallBtn, flexShrink: 0 }}>
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * Copy / Share an invite link. MusterTap doesn't email invites, so the admin
 * sends the link themselves (Share opens the phone's share sheet).
 */
function ShareInvite({ url, name, compact = false }: { url: string; name: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  async function copy() {
    try { await navigator.clipboard.writeText(url); }
    catch { window.prompt('Copy this link', url); return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  async function share() {
    try {
      await navigator.share({ title: 'Join MusterTap', text: `Hi ${name.split(' ')[0]}, here's your invite to MusterTap, the app we use for rosters, cover and checks:`, url });
    } catch { /* cancelled */ }
  }
  return (
    <div style={{ display: 'flex', gap: 6, flexShrink: 0, ...(compact ? {} : { width: '100%', maxWidth: 400, flexWrap: 'wrap' }) }}>
      {!compact && <input readOnly value={url} aria-label="Invite link" onFocus={(e) => e.target.select()}
        style={{ ...inputStyle, flex: '1 1 180px', fontSize: 12 }} />}
      <button type="button" onClick={() => void copy()} title="Copy link" style={{ ...smallBtn, flexShrink: 0 }}>
        <Icon name="link" size={14} /> {copied ? 'Copied ✓' : compact ? 'Copy' : 'Copy link'}
      </button>
      {canShare && (
        <button type="button" onClick={() => void share()} title="Share link" style={{ ...(compact ? smallBtn : addBtn), flexShrink: 0 }}>
          Share
        </button>
      )}
    </div>
  );
}

/** Level, office/UDO flags, qualification chips and positions, on one wrapping line. */
function UnitSummary({ member, catalogue }: { member: OrgMember; catalogue: UnitCatalogue | undefined }) {
  const typeById = new Map(catalogue?.qualifications.map((t) => [t.id, t]));
  const positionNames = member.positionIds
    .map((id) => catalogue?.positions.find((p) => p.id === id)?.name)
    .filter(Boolean);
  const tags = [
    member.level && LEVEL_LABEL[member.level],
    !member.operational && 'Office',
    member.udo && 'UDO',
  ].filter(Boolean);
  if (!tags.length && !member.qualifications.length && !positionNames.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 4 }}>
      {tags.map((t) => (
        <span key={t as string} style={{
          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
          background: theme.status.service.bg, color: theme.status.service.fg,
        }}>{t}</span>
      ))}
      {member.qualifications.map((q) => {
        const t = typeById.get(q.typeId);
        return t ? <QualChip key={q.typeId} code={t.code} grade={q.grade} expiresAt={q.expiresAt} title={t.name} /> : null;
      })}
      {positionNames.map((name) => (
        <span key={name} style={{
          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, whiteSpace: 'nowrap',
          background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}`,
        }}>{name}</span>
      ))}
    </div>
  );
}

function roleLabel(role: UserRole): string {
  return role === 'admin' ? 'Admin' : 'Member';
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
      color: theme.textMuted, textTransform: 'uppercase',
      background: theme.bg, borderBottom: `1px solid ${theme.border}`,
    }}>
      {children}
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '20px 20px', fontSize: 13, color: theme.textMuted, textAlign: 'center' }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 10,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 12px', borderRadius: 8,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.text, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const chip = (on: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : theme.surface,
  color: on ? theme.accentInk : theme.textDim,
});

const iconBtnSmall: React.CSSProperties = {
  width: 26, height: 26, borderRadius: 6,
  border: `1px solid ${theme.border}`,
  background: theme.surface, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: theme.textMuted, flexShrink: 0,
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '10px 0', borderRadius: 10,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const submitBtnStyle: React.CSSProperties = {
  flex: 2, padding: '10px 0', borderRadius: 10,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
};
